// "Jobs that match" on the candidate's Overview (Matt, 2026-10-23): up to ten live postings for their discipline in the
// states they hold a license (else their home state, said as such). Each row opens the posting on the jobs site with
// ?source=crm. Hidden while loading, when the discipline is unknown, and whenever the API errors.
import { useEffect, useState } from 'react';
import { disciplineOf, statesOf, availableFrom, matchWhy } from './lib/jobsMatch';

const where = (j) => [j.city, j.state].filter(Boolean).join(', ');

/** candidate; credentialRows (the Credentials card's rows, or null while loading); load({ discipline, states }) -> { total, jobs } */
export default function JobsMatch({ candidate, credentialRows = [], load }) {
  const discipline = disciplineOf(candidate);
  const { states, from, why, compactVia } = statesOf(candidate, credentialRows || []);
  const startAfter = availableFrom(candidate);                       // 10-23c §2: their available date, when still ahead
  const key = `${candidate && candidate.id}|${discipline}|${states.join(',')}|${startAfter || ''}`;
  const [got, setGot] = useState(null);      // { key, total, jobs } | { key, error }
  useEffect(() => {
    if (!discipline || credentialRows === null) return undefined;
    let live = true;
    load({ discipline, states, startAfter }).then((r) => live && setGot({ key, ...r })).catch(() => live && setGot({ key, error: true }));
    return () => { live = false; };
  }, [key]);   // eslint-disable-line
  if (!discipline || !got || got.key !== key || got.error) return null;
  const own = states.filter((s) => why[s] && why[s].kind !== 'compact');
  const nCompact = states.length - own.length;
  const scope = states.length ? `${own.join(', ')}${from === 'home' ? ' (home state; no license on file)' : ''}${nCompact ? ` + ${nCompact} compact states (${compactVia} license)` : ''}${from === 'preferred' ? ' (the states they asked for)' : ''}` : 'any state';
  return (
    <section className="card" data-testid="jobs-match" aria-labelledby="jobs-match-title" style={{ padding: 16, marginBottom: 16 }}>
      <h3 id="jobs-match-title" style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', margin: '0 0 2px' }}>Jobs that match</h3>
      <p data-testid="jobs-match-scope" style={{ fontSize: 12.5, color: '#475569', margin: '0 0 8px' }}>
        {discipline} · {scope}{startAfter ? ` · starting on or after ${startAfter} (their available date)` : ''} · soonest start first · {got.total > got.jobs.length ? `${got.jobs.length} of ${got.total} open` : `${got.jobs.length} open`}</p>
      {got.jobs.length === 0 ? (
        <p data-testid="jobs-match-none" style={{ fontSize: 13, color: '#475569', margin: 0 }}>No open {discipline} jobs there right now.</p>
      ) : (
        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {got.jobs.map((j) => (
            <li key={j.id} data-testid={`jobs-match-${j.id}`} style={{ display: 'flex', gap: 10, alignItems: 'center', justifyContent: 'space-between', padding: '8px 0', borderTop: '1px solid #f1f5f9' }}>
              <div style={{ minWidth: 0, fontSize: 13 }}>
                <div style={{ fontWeight: 600, color: '#0f172a', overflowWrap: 'anywhere' }}>{j.title}</div>
                <div style={{ color: '#475569', fontSize: 12.5, overflowWrap: 'anywhere' }}>
                  {[where(j), j.setting, j.pay || null, j.start_date ? `starts ${j.start_date}` : null].filter(Boolean).join(' · ')}</div>
                <div data-testid={`jobs-match-why-${j.id}`} style={{ color: '#166534', fontSize: 12, overflowWrap: 'anywhere' }}>Matched: {matchWhy(j, why, startAfter)}</div>
              </div>
              {j.apply_url && <a data-testid={`jobs-match-open-${j.id}`} href={j.apply_url} target="_blank" rel="noopener noreferrer"
                aria-label={`Open ${j.title}${where(j) ? ` in ${where(j)}` : ''}`}
                style={{ flex: 'none', display: 'inline-flex', alignItems: 'center', minHeight: 44, padding: '0 12px', borderRadius: 8, background: '#eff6ff', color: '#1d4ed8', fontWeight: 700, fontSize: 13, textDecoration: 'none' }}>Open</a>}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
